import React, { useState, ComponentProps } from "react";
import { Modal } from "../Modal";
import {
    ChangeRoleModalContainer,
    ChangeRoleModalContentContainer,
    ChangeRoleModalTitle,
    ChangeRoleModalContentWrapper,
    Form,
    Checkbox,
    CheckboxLabel,
    Button,
} from "./ChangeRoleModal.styles";
import { useLanguage } from "../../../core/translations/LanguageContext";
import { ChangeRoleModalTranslations } from "./translations/types";
import { enTranslations } from "./translations/en";
import { deTranslations } from "./translations/de";
import { SupportedLanguage } from "../../../core/translations/supportedLanguages";
import { UserRow } from "../../../core/ServerApiLayer/VOne/institutions/get_users";

const TRANSLATIONS: Record<SupportedLanguage, ChangeRoleModalTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

const ChangeRoleModal = (
    props: Omit<ComponentProps<typeof Modal>, "children"> & {
        selectedUser: UserRow;
        handleAssignRole: (userId: number, roleName: string) => Promise<void>;
        handleUnassignRole: (userId: number, roleName: string) => Promise<void>;
    },
) => {
    const { language } = useLanguage();
    const translations = TRANSLATIONS[language];

    // The user's roles reach this modal already flattened into a comma-separated
    // string (e.g. "ADMIN, TEACHER"), so a strict equality check would never
    // pre-tick a box for a multi-role user — test membership instead.
    const currentRoles = props.selectedUser.role ?? "";

    const [roles, setRoles] = useState({
        admin: currentRoles.includes("ADMIN"),
        teacher: currentRoles.includes("TEACHER"),
    });

    const handleRoleChange = (role: keyof typeof roles) => {
        setRoles((prevRoles) => ({
            ...prevRoles,
            [role]: !prevRoles[role],
        }));
    };

    const handleSubmit = async () => {
        const { selectedUser, handleAssignRole, handleUnassignRole } = props;

        const roleActions = [
            { role: "ADMIN", shouldAssign: roles.admin },
            { role: "TEACHER", shouldAssign: roles.teacher },
        ];

        for (const { role, shouldAssign } of roleActions) {
            if (shouldAssign) {
                await handleAssignRole(selectedUser.id, role);
            } else {
                await handleUnassignRole(selectedUser.id, role);
            }
        }

        props.onRequestClose?.();
    };

    return (
        <Modal {...props}>
            <ChangeRoleModalContainer>
                <ChangeRoleModalContentContainer>
                    <ChangeRoleModalContentWrapper>
                        <Form onSubmit={(e) => e.preventDefault()}>
                            <ChangeRoleModalTitle>
                                {translations.title}
                            </ChangeRoleModalTitle>
                            <CheckboxLabel>
                                <Checkbox
                                    type="checkbox"
                                    checked={roles.admin}
                                    onChange={() => handleRoleChange("admin")}
                                />
                                {translations.options.Admin}
                            </CheckboxLabel>
                            <CheckboxLabel>
                                <Checkbox
                                    type="checkbox"
                                    checked={roles.teacher}
                                    onChange={() => handleRoleChange("teacher")}
                                />
                                {translations.options.Teacher}
                            </CheckboxLabel>
                            <Button type="button" onClick={handleSubmit}>
                                {translations.submit}
                            </Button>
                        </Form>
                    </ChangeRoleModalContentWrapper>
                </ChangeRoleModalContentContainer>
            </ChangeRoleModalContainer>
        </Modal>
    );
};

export default ChangeRoleModal;
